import { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { Loader2, Calendar } from "lucide-react";
import { REASONS } from "../../lib/data";
import { createAppointment } from "../../lib/api";

const initial = {
  full_name: "",
  contact_number: "",
  email: "",
  reason: "",
  message: "",
};

export const AppointmentForm = ({ variant = "light", testidPrefix = "appt", compact = false }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.contact_number.trim() || !form.reason) {
      toast.error("Please fill name, contact and reason.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        contact_number: form.contact_number.trim(),
        reason: form.reason,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.message.trim()) payload.message = form.message.trim();
      await createAppointment(payload);
      toast.success("Request received. Dr. Venkat's team will be in touch shortly.");
      setForm(initial);
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try calling us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const fieldCls = isDark
    ? "bg-transparent border-bone/25 text-bone placeholder:text-bone/40 focus-visible:ring-terra h-12"
    : "bg-paper border-line text-ink placeholder:text-ink-soft/60 focus-visible:ring-forest h-12";
  const labelCls = isDark ? "text-bone/70 text-[11px] tracking-[0.2em] uppercase" : "text-ink-soft text-[11px] tracking-[0.2em] uppercase";

  if (done) {
    return (
      <div data-testid={`${testidPrefix}-success`} className={`text-center py-10 ${isDark ? "text-white" : "text-ink"}`}>
        <p className="eyebrow mb-3">Confirmation</p>
        <h3 className="font-sans font-bold text-2xl mb-3">Thank you.</h3>
        <p className={`text-sm ${isDark ? "text-white/70" : "text-ink-soft"}`}>
          Your consultation request has been received. The team will reach out within a few hours.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid={`${testidPrefix}-new-request`}
          className={`mt-6 underline underline-offset-4 text-sm font-medium ${isDark ? "text-terra-soft" : "text-forest"}`}
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={compact ? "space-y-4" : "space-y-5"} data-testid={`${testidPrefix}-form`}>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full Name" className={labelCls}>
          <Input
            data-testid={`${testidPrefix}-name`}
            value={form.full_name}
            onChange={onChange("full_name")}
            placeholder="Your full name"
            className={fieldCls}
            required
          />
        </Field>
        <Field label="Contact Number" className={labelCls}>
          <Input
            data-testid={`${testidPrefix}-phone`}
            value={form.contact_number}
            onChange={onChange("contact_number")}
            placeholder="+91 ..."
            className={fieldCls}
            required
          />
        </Field>
      </div>

      <Field label="Email Address (optional)" className={labelCls}>
        <Input
          type="email"
          data-testid={`${testidPrefix}-email`}
          value={form.email}
          onChange={onChange("email")}
          placeholder="you@example.com"
          className={fieldCls}
        />
      </Field>

      <Field label="Reason for Consultation" className={labelCls}>
        <Select value={form.reason} onValueChange={(v) => setForm((f) => ({ ...f, reason: v }))}>
          <SelectTrigger
            data-testid={`${testidPrefix}-reason-trigger`}
            className={fieldCls}
          >
            <SelectValue placeholder="Select consultation type" />
          </SelectTrigger>
          <SelectContent>
            {REASONS.map((r) => (
              <SelectItem key={r} value={r} data-testid={`${testidPrefix}-reason-${r}`}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Message (optional)" className={labelCls}>
        <Textarea
          data-testid={`${testidPrefix}-message`}
          value={form.message}
          onChange={onChange("message")}
          rows={3}
          placeholder="Briefly describe your concern..."
          className={`${fieldCls.replace("h-12", "min-h-[96px] py-3")}`}
        />
      </Field>

      <button
        type="submit"
        disabled={loading}
        data-testid={`${testidPrefix}-submit`}
        className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold tracking-wide transition disabled:opacity-60 ${
          isDark
            ? "bg-terra hover:bg-terra-soft text-paper"
            : "btn-primary"
        }`}
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Calendar size={16} />}
        {loading ? "Submitting..." : "Submit Appointment Request"}
      </button>

      <p className={`text-[11px] ${isDark ? "text-white/50" : "text-ink-soft"}`}>
        By submitting, you agree to be contacted by the practice regarding your enquiry.
      </p>
    </form>
  );
};

const Field = ({ label, className, children }) => (
  <div>
    <Label className={`mb-1.5 block ${className}`}>{label}</Label>
    {children}
  </div>
);
